<script setup>
import axios from '@/helpers/axios'
import { LControlLayers, LGeoJson, LMap, LTileLayer } from "@vue-leaflet/vue-leaflet"
import "leaflet/dist/leaflet.css"
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import moment from "moment";

definePage({
  meta: {
    layout: 'default',
    role: 'user'
  },
})

const parcels = ref([])
const accessToken = ref('pk.eyJ1IjoicmVuYXRvbXMiLCJhIjoiY2pzcDNpaDdlMHFvcTQ5cWx3eXJlMDZ2eiJ9.3GsYRV5ahGFqCpb01u7qFw')
const urlSattelite = ref('https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=' + accessToken.value)

const getData = () => {
  axios.get('/satellite').then(r => {
    parcels.value = r.data.data
  }).catch(e => {
    console.error(e)
  })
}

getData()

const onMapReady = (map, bounds) => {
  map.fitBounds(bounds)
}
</script>

<template>
  <div>
    <div class="page-header mb-4">
      <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between gap-4">
        <div>
          <h2 class="text-h4 font-weight-semibold mb-2 text-high-emphasis">
            {{ $t('satellite.title') }}
          </h2>
          <p class="text-body-1 text-medium-emphasis mb-0">
            {{ $t('satellite.description') }}
          </p>
        </div>
      </div>
    </div>

    <VRow v-if="parcels.length" class="match-height">
      <VCol
        v-for="(item, i) in parcels"
        :key="item.uuid"
        cols="12"
        sm="6"
        lg="4"
      >
        <VCard
          class="overflow-visible group-card"
          style="border-radius: 16px;"
          elevation="2"
        >
          <div class="pa-4">
            <!-- Header -->
            <div class="d-flex justify-space-between align-start mb-4">
              <div>
                <h4 class="text-h5 font-weight-semibold mb-1 text-high-emphasis">
                  {{ item.name }}
                </h4>
                <span
                  class="text-caption px-2 py-1 rounded-lg bg-primary text-white"
                >
                  {{ moment.utc(item.sampled_at).local().format('Y-MM-DD HH:mm') }}
                </span>
              </div>

              <VBtn
                icon
                size="small"
                variant="tonal"
                color="primary"
                :to="{ name: 'satellite-uuid', params: { uuid: item.uuid } }"
              >
                <VIcon icon="tabler-arrow-right" size="x-large"/>
                <VTooltip activator="parent" location="top">
                  {{ $t('layout.open') }}
                </VTooltip>
              </VBtn>
            </div>

            <!-- Middle section -->
            <div>
              <div class="d-flex align-center mb-1">
                <VIcon icon="tabler-ruler-measure" size="18" class="me-2 text-primary" />
                <span class="text-body-2 text-medium-emphasis">
                  {{ $t('satellite.area') }}: {{ item.area }} ha
                </span>
              </div>

            </div>
          </div>

          <!-- Middle section -->
          <div style="overflow: hidden; border-bottom-left-radius: 16px; border-bottom-right-radius: 16px">
           <LMap
              :ref="'map-' + i"
              :zoom="10"
              :center="[0,0]"
              :max-bounds="item.bounds"
              :use-global-leaflet="false"
              style="height: 250px; width: 100%;"
              :options="{scrollWheelZoom:false}"
              @ready="(map) => onMapReady(map, item.bounds)"
            >
              <LTileLayer
                :url="urlSattelite"
                layer-type="base"
                name="Mapbox Satellite"
                visible
              />
              <LGeoJson
                v-if="item.geojson"
                :geojson="item.geojson"
                layer-type="overlay"
              />
            </LMap>
          </div>
        </VCard>
      </VCol>
    </VRow>
    <div v-else>
      <div
        class="d-flex flex-column align-center justify-center text-center flex-grow-1 py-10"
      >
        <VIcon icon="tabler-satellite-off" size="48" color="grey" class="mb-3" />
        <h4 class="text-h6 font-weight-medium text-high-emphasis mb-1">
          {{ $t('satellite.no_parcels') }}
        </h4>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-header {
  background: linear-gradient(180deg, var(--v-theme-surface), transparent);
  border-radius: 16px;
  padding: 1.5rem 0rem;
  transition: all 0.25s ease;
}

@media (max-width: 600px) {
  .page-header {
    padding: 1.25rem 1rem;
  }
  .page-header h2 {
    font-size: 1.4rem;
  }
}

.group-card {
  transition: all 0.25s ease;
}

.group-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  border-color: var(--v-theme-primary);
}
</style>
